import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import { ShareButton } from '../components/ShareAutomation';
import { generateWorkflowFromTemplate } from '../utils/workflowGenerator';

import {
  ArrowLeft,
  ArrowRight,
  ArrowSquareOut,
  CaretRight,
  CheckCircle,
  CircleNotch,
  Clock,
  Download,
  FileArrowDown,
  FileText,
  Flame,
  GearSix,
  Hammer,
  Headphones,
  Info,
  Lightning,
  List,
  Megaphone,
  Microscope,
  PlugsConnected,
  Robot,
  SealCheck,
  ShareNetwork,
  SignIn,
  Sparkle,
  Stack,
  Star,
  Tag,
  TrendUp,
  UsersThree,
  Wallet,
  Handshake
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';
import { getWorkerIcon } from '../utils/workerIcons';
import { extractUsedApps, getAppLogo, getGenericAppIcon } from '../utils/usedApps';

const OUTCOME_ICONS = {
  'Lead & Enquiries': Lightning,
  'Bookings & Scheduling': Clock,
  'Quotes & Revenue': TrendUp,
  'Payments & Admin': Wallet,
  'Reviews & Reputation': Star,
  'Customer Retention': UsersThree,
  'Operations': GearSix,
  'Advanced': Hammer,
  // Legacy fallbacks
  'Increase Sales': TrendUp,
  'Save Time': Clock,
  'Reduce Admin': FileText,
  'Customer Support': Headphones,
  'Marketing': Megaphone,
  'Finance & Admin': Wallet,
  'Research & Analysis': Microscope,
};

const OUTCOME_COLORS = {
  'Lead & Enquiries': { bg: 'rgba(16,185,129,0.12)', text: '#34D399', border: 'rgba(16,185,129,0.25)' },
  'Bookings & Scheduling': { bg: 'rgba(59,130,246,0.12)', text: '#60A5FA', border: 'rgba(59,130,246,0.25)' },
  'Quotes & Revenue': { bg: 'rgba(16,185,129,0.12)', text: '#10B981', border: 'rgba(16,185,129,0.25)' },
  'Payments & Admin': { bg: 'rgba(250,204,21,0.12)', text: '#FACC15', border: 'rgba(250,204,21,0.25)' },
  'Reviews & Reputation': { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B', border: 'rgba(245,158,11,0.25)' },
  'Customer Retention': { bg: 'rgba(139,92,246,0.12)', text: '#A78BFA', border: 'rgba(139,92,246,0.25)' },
  'Operations': { bg: 'rgba(6,182,212,0.12)', text: '#22D3EE', border: 'rgba(6,182,212,0.25)' },
  'Advanced': { bg: 'rgba(100,116,139,0.12)', text: '#94A3B8', border: 'rgba(100,116,139,0.25)' },
  // Legacy fallbacks
  'Increase Sales': { bg: 'rgba(16,185,129,0.12)', text: '#10B981', border: 'rgba(16,185,129,0.25)' },
  'Save Time': { bg: 'rgba(59,130,246,0.12)', text: '#3B82F6', border: 'rgba(59,130,246,0.25)' },
  'Reduce Admin': { bg: 'rgba(168,85,247,0.12)', text: '#A855F7', border: 'rgba(168,85,247,0.25)' },
  'Customer Support': { bg: 'rgba(0,229,192,0.12)', text: '#00E5C0', border: 'rgba(0,229,192,0.25)' },
  'Marketing': { bg: 'rgba(249,115,22,0.12)', text: '#F97316', border: 'rgba(249,115,22,0.25)' },
  'Finance & Admin': { bg: 'rgba(234,179,8,0.12)', text: '#EAB308', border: 'rgba(234,179,8,0.25)' },
  'Research & Analysis': { bg: 'rgba(20,184,166,0.12)', text: '#14B8A6', border: 'rgba(20,184,166,0.25)' },
};

const CATEGORY_DISPLAY = {
  'Lead & Enquiries': 'Capture & Convert Leads',
  'Bookings & Scheduling': 'Automate Bookings',
  'Quotes & Revenue': 'Win More Revenue',
  'Payments & Admin': 'Get Paid Faster',
  'Reviews & Reputation': 'Build Your Reputation',
  'Customer Retention': 'Keep Customers Coming Back',
  'Operations': 'Streamline Operations',
  'Advanced': 'Advanced / Power Tools',
};

const getCatDisplay = (dbName) => CATEGORY_DISPLAY[dbName] || dbName;

/* ═══════════════════════════════════════════════════════
   STANDARDIZED TEMPLATE OVERRIDES
   Keyed by slug. When a template matches, these sections
   replace the generic "About", "How this works", "What you get".
   ═══════════════════════════════════════════════════════ */
const EXAMPLE_STEP_STYLES = {
  input:  { bg: 'bg-[rgba(0,229,192,0.06)]', border: 'border-[rgba(0,229,192,0.10)]', text: 'text-[#00E5C0]', badge: 'Input',  badgeBg: 'bg-[rgba(0,229,192,0.1)]' },
  logic:  { bg: 'bg-[rgba(0,229,192,0.04)]', border: 'border-[rgba(0,229,192,0.08)]', text: 'text-[#00C9A7]', badge: 'Logic',  badgeBg: 'bg-[rgba(0,229,192,0.08)]' },
  ai:     { bg: 'bg-[rgba(139,143,245,0.05)]', border: 'border-[rgba(139,143,245,0.10)]', text: 'text-[#A5A8FC]', badge: 'AI',     badgeBg: 'bg-[rgba(139,143,245,0.08)]' },
  action: { bg: 'bg-[rgba(0,229,192,0.06)]', border: 'border-[rgba(0,229,192,0.10)]', text: 'text-[#00E5C0]', badge: 'Action', badgeBg: 'bg-[rgba(0,229,192,0.1)]' },
};

const MARKETPLACE_TEMPLATE_OVERRIDES = {};

/* Tool name mapping from backend slugs to display names */
const TOOL_DISPLAY_MAP = {
  gmail: 'Gmail',
  google_calendar: 'Google Calendar',
  googlecalendar: 'Google Calendar',
  airtable: 'Airtable CRM',
  twilio: 'Twilio SMS',
  ai: 'AI',
  platform: 'Platform',
  webhook: 'Webhook',
};

const STEP_TYPE_MAP = {
  trigger: 'input',
  ai: 'ai',
  action: 'action',
  logic: 'logic',
};

/* Convert backend template definition to frontend sections */
function buildSectionsFromDef(def) {
  const sections = [];

  sections.push({
    heading: 'What this does',
    type: 'paragraph',
    content: def.what_this_worker_does,
  });

  if (def.trigger) {
    sections.push({
      heading: 'When it runs',
      type: 'list',
      items: [def.trigger.label],
    });
  }

  if (def.steps?.length > 0) {
    sections.push({
      heading: 'How this runs',
      type: 'workflow',
      steps: def.steps.map((s) => ({
        label: s.label,
        stepType: STEP_TYPE_MAP[s.type] || 'action',
        tools: s.provider && s.provider !== 'ai' && s.provider !== 'platform'
          ? [TOOL_DISPLAY_MAP[s.provider] || s.provider]
          : [],
        connectionAvailable: s.connection_available,
        requiresConnection: s.requires_connection,
        fallback: s.fallback,
      })),
    });
  }

  sections.push({
    heading: 'What happens',
    type: 'ordered',
    items: (def.steps || [])
      .filter((s) => s.type !== 'trigger')
      .map((s) => s.label + (s.fallback ? ` (or: ${s.fallback})` : '')),
  });

  const toolItems = [
    ...(def.required_tools || []).map((t) => `${TOOL_DISPLAY_MAP[t] || t} (required)`),
    ...(def.optional_tools || []).map((t) => `${TOOL_DISPLAY_MAP[t] || t} (optional)`),
  ];
  if (toolItems.length > 0) {
    sections.push({ heading: 'What you need', type: 'list', items: toolItems });
  }

  if (def.readiness) {
    sections.push({
      heading: 'Readiness',
      type: 'list',
      items: [def.readiness.note],
    });
  }

  if (def.limitations?.length > 0) {
    sections.push({ heading: 'Limitations', type: 'list', items: def.limitations });
  }

  return sections;
}

const TagPill = ({ items }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((item, i) => (
      <span
        key={i}
        className="px-2.5 py-1 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-xs"
      >
        {item.replace(/_/g, ' ').replace(/file /g, '')}
      </span>
    ))}
  </div>
);

const Section = ({ title, icon: Icon, children, testId }) => (
  <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] p-6 mb-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon size={15} weight="duotone" className="text-[#8b8fa3]" />}
      <h2 className="text-sm font-semibold text-white">{title}</h2>
    </div>
    {children}
  </div>
);

const MarkdownText = ({ text }) => {
  if (!text) return null;
  const lines = text.split('\n').filter(l => l.trim());
  return (
    <div className="space-y-2 text-sm text-[#c0c4d0] leading-relaxed">
      {lines.map((line, i) => {
        const cleaned = line.replace(/\*\*/g, '');
        if (line.startsWith('- **')) {
          const match = line.match(/^- \*\*(.+?)\*\*(.*)$/);
          if (match) {
            return (
              <div key={i} className="flex items-start gap-2 pl-1">
                <span className="text-[#555] mt-1.5 shrink-0 w-1 h-1 rounded-full bg-[#555]" />
                <p><span className="text-white font-medium">{match[1]}</span>{match[2]}</p>
              </div>
            );
          }
        }
        if (line.startsWith('- ')) {
          return (
            <div key={i} className="flex items-start gap-2 pl-1">
              <span className="text-[#555] mt-1.5 shrink-0 w-1 h-1 rounded-full bg-[#555]" />
              <p>{cleaned.substring(2)}</p>
            </div>
          );
        }
        return <p key={i}>{cleaned}</p>;
      })}
    </div>
  );
};

const RatingWidget = ({ slug, initialRating, initialCount }) => {
  const { user } = useAuth();
  const [hoveredStar, setHoveredStar] = useState(0);
  const [yourRating, setYourRating] = useState(null);
  const [avgRating, setAvgRating] = useState(initialRating);
  const [ratingCount, setRatingCount] = useState(initialCount);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetch(`${BACKEND_URL}/api/public/automations/${slug}/rating`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setAvgRating(d.avg_rating || 0);
          setRatingCount(d.rating_count || 0);
          if (d.your_rating) setYourRating(d.your_rating);
        }
      })
      .catch(() => {});
  }, [slug, user]);

  const submitRating = async (value) => {
    if (!user || saving) return;
    setSaving(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/public/automations/${slug}/rate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: value }),
      });
      if (resp.ok) {
        const d = await resp.json();
        setYourRating(d.your_rating);
        setAvgRating(d.avg_rating);
        setRatingCount(d.rating_count);
      }
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] p-5 mb-5" data-testid="section-rating">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-xs font-semibold text-[#8b8fa3] uppercase tracking-wider">Rating</h3>
          {avgRating > 0 && (
            <span className="text-xs text-[#8b8fa3]">
              <Star size={12} weight="fill" className="text-amber-400 inline -mt-0.5 mr-0.5" />
              {avgRating.toFixed(1)} ({ratingCount} {ratingCount === 1 ? 'rating' : 'ratings'})
            </span>
          )}
          {ratingCount === 0 && (
            <span className="text-xs text-[#555]">No ratings yet</span>
          )}
        </div>
        {user && (
          <div className="flex items-center gap-1" data-testid="rating-stars">
            {[1, 2, 3, 4, 5].map(v => (
              <button
                key={v}
                onClick={() => submitRating(v)}
                onMouseEnter={() => setHoveredStar(v)}
                onMouseLeave={() => setHoveredStar(0)}
                disabled={saving}
                className="p-0.5 transition-transform hover:scale-110 disabled:opacity-50"
                data-testid={`rate-star-${v}`}
              >
                <Star
                  size={18}
                  weight={(hoveredStar >= v || (!hoveredStar && yourRating >= v)) ? 'fill' : 'regular'}
                  className={`transition-colors ${(hoveredStar >= v || (!hoveredStar && yourRating >= v)) ? 'text-amber-400' : 'text-[#444]'}`}
                />
              </button>
            ))}
            {yourRating && (
              <span className="text-[10px] text-[#6b7080] ml-1.5">Your rating: {yourRating}</span>
            )}
          </div>
        )}
        {!user && (
          <Link to="/login" className="text-[10px] text-[#00E5C0] hover:text-[#00CCB0] transition-colors">
            Sign in to rate
          </Link>
        )}
      </div>
    </div>
  );
};


export default function AutomationDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, authStatus } = useAuth();
  const isAuthenticated = authStatus === 'authenticated' && user;
  const autoInstallTriggered = useRef(false);

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectedApps, setConnectedApps] = useState(new Set());
  const [templateDef, setTemplateDef] = useState(null);

  useEffect(() => {
    fetchTemplate();
  }, [slug]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${BACKEND_URL}/api/service-os/integrations/apps`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const connected = new Set();
        [...(data.popular || []), ...(data.all || [])].forEach(app => {
          if (app.connected) connected.add(app.name);
        });
        setConnectedApps(connected);
      })
      .catch(() => {});
  }, [isAuthenticated]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${BACKEND_URL}/api/public/automations/${slug}`);
      if (res.status === 404) { setError('not_found'); return; }
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setTemplate(data.template);

      // Fetch standardized template definition
      try {
        const defRes = await fetch(`${BACKEND_URL}/api/worker-templates/${slug}`, { credentials: 'include' });
        if (defRes.ok) {
          const defData = await defRes.json();
          setTemplateDef(defData);
        }
      } catch (_) { /* Non-standardized template — use legacy view */ }
    } catch (err) {
      console.error('Failed to load automation:', err);
      setError('error');
    } finally {
      setLoading(false);
    }
  };

  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;
  const [installing, setInstalling] = useState(false);

  // Auto-install: when user returns from login/register with install intent
  useEffect(() => {
    if (autoInstallTriggered.current) return;
    const hasAutoInstall = searchParams.get('auto_install') === 'true';
    const storedIntent = localStorage.getItem('install_intent');
    if (!hasAutoInstall && !storedIntent) return;
    if (!isAuthenticated || !orgId || !template) return;

    // Check stored intent matches this slug
    if (storedIntent) {
      try {
        const intent = JSON.parse(storedIntent);
        if (intent.slug !== slug) return;
        // Expire intents older than 30 minutes
        if (Date.now() - intent.timestamp > 30 * 60 * 1000) {
          localStorage.removeItem('install_intent');
          return;
        }
      } catch { return; }
    }

    autoInstallTriggered.current = true;
    localStorage.removeItem('install_intent');
    localStorage.removeItem('post_auth_redirect');
    // Clean the URL param without navigation
    searchParams.delete('auto_install');
    setSearchParams(searchParams, { replace: true });
    // Trigger install
    handleInstall();
  }, [isAuthenticated, orgId, template]);

  const handleInstall = async () => {
    if (!isAuthenticated) {
      // Store install intent for post-login auto-install
      localStorage.setItem('install_intent', JSON.stringify({ slug, timestamp: Date.now() }));
      localStorage.setItem('post_auth_redirect', `/marketplace/${slug}?auto_install=true`);
      navigate(`/login?redirect=${encodeURIComponent(`/marketplace/${slug}?auto_install=true`)}`);
      return;
    }
    if (!orgId || installing) return;
    setInstalling(true);

    try {
      // 1. Instantiate the agent template
      const instRes = await fetchWithOrg(
        `${BACKEND_URL}/api/agent-templates/${template.id}/instantiate`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tool_preferences: template.works_with || [] }) },
        orgId
      );
      if (!instRes.ok) throw new Error('Failed to create worker');
      const instData = await instRes.json();

      // 2. Determine automation type from template metadata
      const isFileAnalysis = template.capability_profile === 'analysis_agent' ||
        template.automation_type === 'file_analysis' ||
        template.worker_type === 'file_analysis' ||
        template.trigger_type === 'file_upload';

      // 3. Generate workflow steps — use file analysis steps for analysis workers
      let workflowSteps;
      let workerConfig = null;

      if (isFileAnalysis) {
        workflowSteps = [
          { type: 'input', label: (template.how_it_works_steps || [])[0] || 'Receive uploaded file' },
          ...((template.how_it_works_steps || []).slice(1, -1)).map(s => ({ type: 'action', label: s })),
          { type: 'output', label: (template.how_it_works_steps || []).slice(-1)[0] || 'Generate results', outputDest: 'activity' },
        ];
        const acceptedInputs = template.accepted_inputs || ['pdf', 'docx', 'txt'];
        const typeMap = { pdf: 'doc', docx: 'doc', doc: 'doc', txt: 'doc', png: 'image', jpg: 'image', jpeg: 'image', image: 'image' };
        const acceptedTypes = [...new Set(acceptedInputs.map(t => typeMap[t] || 'doc'))];
        workerConfig = {
          acceptedTypes,
          ref_input_type: 'text_and_file',
          ref_required: false,
          evaluation_mode: 'criteria_based',
          output_format: 'Scorecard',
          include_evidence: true,
          export_format: 'pdf',
          instructions: template.description || '',
          task_instructions: template.description || '',
          scoring_schema: [
            { name: 'Content Understanding', weight: 40, notes: 'Depth of knowledge, accuracy, and critical thinking' },
            { name: 'Structure & Clarity', weight: 25, notes: 'Logical flow, clear arguments, and organisation' },
            { name: 'Grammar & Language', weight: 20, notes: 'Writing quality, spelling, and academic tone' },
            { name: 'Referencing / Evidence', weight: 15, notes: 'Use of sources, citations, and supporting evidence' },
          ],
        };
      } else {
        workflowSteps = generateWorkflowFromTemplate(template);
      }

      // 4. Create automation with correct type and config
      const autoRes = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/automations`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: template.name,
            automation_type: isFileAnalysis ? 'file_analysis' : (template.automation_type || 'template'),
            steps: workflowSteps.map(s => ({ type: s.type, label: s.label, app: s.app || null, outputDest: s.outputDest || undefined })),
            status: 'draft',
            requires_review: true,
            tool_preferences: template.works_with || [],
            outcome_id: template.outcome_category || null,
            agent_id: instData.agent_id,
            worker_config: workerConfig,
          }),
        },
        orgId
      );
      let workerId = instData.agent_id;
      if (autoRes.ok) {
        const autoData = await autoRes.json();
        workerId = autoData.automation?.id || instData.agent_id;
      }

      toast.success(`${template.name} installed`);
      navigate(`/workers/${workerId}/detail`);
    } catch (err) {
      toast.error(err.message || 'Failed to install worker');
    } finally {
      setInstalling(false);
    }
  };

  const handleBuildSimilar = () => handleInstall();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#161B26]">
        <LandingHeader />
        <div className="flex items-center justify-center pt-40">
          <div className="w-8 h-8 border-2 border-[#00E5C0] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error === 'not_found' || !template) {
    return (
      <div className="min-h-screen bg-[#161B26]">
        <LandingHeader />
        <div className="flex flex-col items-center justify-center pt-40 px-6" data-testid="worker-not-found">
          <h1 className="text-2xl font-bold text-white mb-3">Worker not found</h1>
          <p className="text-[#8b8fa3] text-sm mb-6">This worker may have been removed or the URL is incorrect.</p>
          <Link to="/workers" className="text-[#00E5C0] text-sm hover:underline flex items-center gap-1">
            <ArrowLeft size={14} weight="bold" /> Back to gallery
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const color = OUTCOME_COLORS[template.outcome_category] || OUTCOME_COLORS['Operations'];
  const OutcomeIcon = OUTCOME_ICONS[template.outcome_category] || GearSix;

  // Parse description for older templates that embed markdown sections
  const hasRichFields = !!(template.about || template.what_this_agent_does || template.how_it_works);
  const cleanDescription = template.description?.replace(/\*\*/g, '') || '';
  const descriptionParagraphs = cleanDescription.split('\n').filter(p => p.trim());

  // Integrations — split into primary and optional for new UX
  const allWorksWithTools = template.works_with || [];
  const CORE_TOOLS = new Set(['Gmail', 'Outlook', 'Google Calendar', 'Airtable', 'Twilio']);
  const primaryTools = allWorksWithTools.filter(t => CORE_TOOLS.has(t));
  const optionalTools = allWorksWithTools.filter(t => !CORE_TOOLS.has(t));

  const handleToolClick = (toolName) => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }
    navigate('/apps');
  };

  // Capabilities — canonical from enrichment
  const capabilities = template.marketplace_capabilities || [];

  // Complexity
  const complexity = template.complexity || (template.difficulty_level ? template.difficulty_level.charAt(0).toUpperCase() + template.difficulty_level.slice(1) : 'Beginner');
  const complexityCfg = {
    'Beginner': { color: '#10B981', bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.20)' },
    'Intermediate': { color: '#F97316', bg: 'rgba(249,115,22,0.10)', border: 'rgba(249,115,22,0.20)' },
    'Advanced': { color: '#F59E0B', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.20)' },
  }[complexity] || { color: '#10B981', bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.20)' };

  // Preview examples
  const previewExamples = template.preview_examples || [];

  // Rating
  const avgRating = template.avg_rating || 0;
  const ratingCount = template.rating_count || 0;

  // Source badge config
  const badgeCfg = {
    verified: { icon: SealCheck, color: '#60A5FA', label: template.badge_label || 'Man & Machine Verified' },
    partner: { icon: Handshake, color: '#A78BFA', label: template.badge_label || 'Partner' },
    community: { icon: UsersThree, color: '#6b7080', label: template.badge_label || 'Community' },
  }[template.verification_badge] || { icon: UsersThree, color: '#6b7080', label: 'Community' };
  const BadgeIcon = badgeCfg.icon;

  // Typical use cases — from new field or legacy use_cases
  const useCases = template.typical_use_cases?.length > 0
    ? template.typical_use_cases
    : template.use_cases || [];

  return (
    <>
      <SEO
        title={`${template.name} | Worker Marketplace | Man & Machine`}
        description={template.short_description || template.description?.substring(0, 160)}
      />
      <div className="min-h-screen bg-[#161B26]" data-testid="worker-detail-page">
        <LandingHeader />

        <div className="pt-24 pb-20 px-6">
          <div className="max-w-5xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-[#555] mb-8" data-testid="worker-breadcrumb">
              <Link to="/marketplace" className="hover:text-[#8b8fa3] transition-colors">Marketplace</Link>
              <CaretRight size={12} />
              <span className="text-[#8b8fa3]">{getCatDisplay(template.outcome_category)}</span>
              <CaretRight size={12} />
              <span className="text-white">{template.name}</span>
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* ── Hero Section with glassmorphism ── */}
              <div className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-gradient-to-br from-[#111419] via-[#12161C] to-[#0D1117] p-8 sm:p-10 mb-10 relative overflow-hidden">
                {/* Subtle glow */}
                <div className="absolute top-0 right-0 w-80 h-80 rounded-full opacity-[0.04]" style={{ background: `radial-gradient(circle, ${color.text || '#00E5C0'}, transparent 70%)` }} />

                <div className="flex flex-col lg:flex-row lg:items-start gap-8 relative z-10">
                  {/* Left content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-5">
                      <img src={getWorkerIcon(template.outcome_category, template.name)} alt=""
                        className="w-14 h-14 rounded-2xl object-cover" data-testid="detail-worker-icon" />
                      <span className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: color.bg, color: color.text, border: `1px solid ${color.border}` }}>
                        <OutcomeIcon size={11} weight="duotone" className="inline mr-1 -mt-0.5" />
                        {getCatDisplay(template.outcome_category)}
                      </span>
                    </div>

                    <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-white mb-4 tracking-tight leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {template.name}
                    </h1>
                    {template.short_description && (
                      <p className="text-base sm:text-lg text-[#B0B8C4] leading-relaxed mb-5 max-w-xl">{template.short_description}</p>
                    )}

                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="text-sm text-[#7D8896]">By {template.author}</p>
                      <span data-testid="verification-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium"
                        style={{
                          backgroundColor: `color-mix(in srgb, ${badgeCfg.color} 10%, transparent)`,
                          border: `1px solid color-mix(in srgb, ${badgeCfg.color} 20%, transparent)`,
                          color: badgeCfg.color,
                        }}>
                        <BadgeIcon size={11} weight="duotone" /> {badgeCfg.label}
                      </span>
                      {avgRating > 0 && (
                        <span className="flex items-center gap-1 text-sm text-[#8b8fa3]" data-testid="detail-rating-display">
                          <Star size={13} weight="fill" className="text-amber-400" />
                          {avgRating.toFixed(1)} ({ratingCount})
                        </span>
                      )}
                    </div>

                    {/* This worker uses — inline primary tools */}
                    {primaryTools.length > 0 && (
                      <div className="flex items-center gap-2.5 mt-5 flex-wrap" data-testid="section-primary-tools">
                        <span className="text-[10px] text-[#5C6778] uppercase tracking-widest font-semibold">This worker uses</span>
                        {primaryTools.slice(0, 4).map((name, i) => {
                          const isConnected = connectedApps.has(name);
                          return (
                            <button key={i} onClick={() => handleToolClick(name)} data-testid={`primary-tool-${i}`}
                              className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(0,229,192,0.3)] transition-all cursor-pointer group">
                              <img src={getAppLogo(name)} alt={name} className="w-[18px] h-[18px] rounded object-contain" loading="lazy"
                                onError={e => { e.target.src = getGenericAppIcon(); e.target.onerror = null; }} />
                              <span className="text-xs text-[#c0c4d0] group-hover:text-white transition-colors">{name}</span>
                              {isAuthenticated && isConnected && (
                                <span className="w-2 h-2 rounded-full bg-[#10B981] shrink-0" title="Connected" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Right: Install CTA */}
                  <div className="lg:w-72 shrink-0">
                    <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] backdrop-blur-sm p-6">
                      <button data-testid="use-worker-btn" onClick={handleInstall} disabled={installing}
                        className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-[#00E5C0] hover:bg-[#00CCB0] text-[#12161C] text-sm font-bold transition-colors mb-4 disabled:opacity-60">
                        {installing ? (<><CircleNotch size={16} className="animate-spin" /> Installing...</>) : isAuthenticated ? (<><Download size={16} weight="duotone" /> Use this Worker</>) : (<><SignIn size={16} weight="duotone" /> Sign Up to Install</>)}
                      </button>
                      <div className="space-y-2.5 text-xs text-[#B0B8C4]">
                        <div className="flex items-center gap-2"><CheckCircle size={13} weight="duotone" className="text-[#00E5C0] shrink-0" /><span>Free to install</span></div>
                        <div className="flex items-center gap-2"><CheckCircle size={13} weight="duotone" className="text-[#00E5C0] shrink-0" /><span>Configurable to your needs</span></div>
                        <div className="flex items-center gap-2"><CheckCircle size={13} weight="duotone" className="text-[#00E5C0] shrink-0" /><span>Runs in your workspace</span></div>
                      </div>
                      {template.difficulty_level && (
                        <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.08)] space-y-2">
                          <div className="flex items-center gap-2 text-xs text-[#7D8896]">
                            <Clock size={12} weight="duotone" /> <span>~30s install</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[#7D8896]">
                            <Info size={12} weight="duotone" />
                            <span className="capitalize">{template.difficulty_level} setup</span>
                            {template.estimated_setup_time && <span>· {template.estimated_setup_time}</span>}
                          </div>
                        </div>
                      )}
                      <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.08)] flex items-center justify-between">
                        <ShareButton slug={slug} visibility={template.visibility || 'public_listed'} />
                        {isAuthenticated && (
                          <button data-testid="submit-as-template-btn" onClick={() => navigate(`/my-submissions?prefill=${template.slug}`)}
                            className="text-[10px] text-[#8b8fa3] hover:text-[#00E5C0] transition-colors flex items-center gap-1">
                            <ArrowSquareOut size={10} /> Submit as Worker
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── TEMPLATE SECTIONS ── */}
              {(() => {
                const override = templateDef ? { sections: buildSectionsFromDef(templateDef) } : MARKETPLACE_TEMPLATE_OVERRIDES[slug];
                if (override) {
                  /* ── Standardized sections for upgraded templates ── */
                  return (
                    <div data-testid="standardized-template-sections">
                      {override.sections.map((sec, idx) => (
                        <div key={idx} className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] p-6 mb-5"
                          data-testid={`template-section-${sec.heading.toLowerCase().replace(/\s+/g, '-')}`}>
                          <div className="flex items-center gap-2 mb-4">
                            <h2 className="text-xs font-semibold text-[#7D8896] uppercase tracking-wider">{sec.heading}</h2>
                          </div>

                          {sec.type === 'paragraph' && (
                            <p className="text-sm text-[#C9D1D9] leading-relaxed">{sec.content}</p>
                          )}

                          {sec.type === 'list' && (
                            <ul className="space-y-1.5">
                              {sec.items.map((item, j) => (
                                <li key={j} className="flex items-start gap-2 text-sm text-[#C9D1D9]">
                                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#00E5C0] shrink-0" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )}

                          {sec.type === 'ordered' && (
                            <ol className="space-y-1.5">
                              {sec.items.map((item, j) => (
                                <li key={j} className="flex items-start gap-2.5 text-sm text-[#C9D1D9]">
                                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[rgba(0,229,192,0.08)] text-[#00E5C0] text-[10px] font-bold flex items-center justify-center shrink-0">{j + 1}</span>
                                  {item}
                                </li>
                              ))}
                            </ol>
                          )}

                          {sec.type === 'workflow' && (
                            <div data-testid="example-workflow-visual">
                              <div className="flex items-center mb-3">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-[#7D8896]">
                                  <ShareNetwork size={11} /> Example workflow
                                </span>
                              </div>

                              <div className="space-y-0">
                                {sec.steps.map((ws, wi) => {
                                  const style = EXAMPLE_STEP_STYLES[ws.stepType] || EXAMPLE_STEP_STYLES.action;
                                  const stepTools = (ws.tools || []).slice(0, 3);
                                  const isAI = ws.stepType === 'ai';

                                  return (
                                    <div key={wi}>
                                      <div className={`flex items-center gap-3 p-3 rounded-xl ${style.bg} border ${style.border}`}
                                        data-testid={`example-step-${wi}`}>
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${style.text} bg-[rgba(255,255,255,0.06)]`}>
                                          {wi + 1}
                                        </div>
                                        <span className={`text-sm font-medium flex-1 ${style.text}`}>{ws.label}</span>
                                        {/* Tool logos on the right */}
                                        {stepTools.length > 0 && (
                                          <div className="flex items-center gap-1.5 shrink-0">
                                            {stepTools.map((toolName) => (
                                              <img key={toolName} src={getAppLogo(toolName)} alt={toolName}
                                                className="w-5 h-5 rounded object-contain"
                                                onError={(e) => { e.target.style.display = 'none'; }} />
                                            ))}
                                          </div>
                                        )}
                                        {/* AI indicator on the right */}
                                        {isAI && stepTools.length === 0 && (
                                          <div className="w-5 h-5 rounded flex items-center justify-center bg-[rgba(139,143,245,0.12)] shrink-0">
                                            <Sparkle size={11} weight="fill" className="text-[#A5A8FC]" />
                                          </div>
                                        )}
                                        <span className={`text-[9px] uppercase tracking-wider shrink-0 px-2 py-1 rounded font-semibold ${style.badgeBg} ${style.text}`}>
                                          {style.badge}
                                        </span>
                                        {/* Connection status dot */}
                                        {ws.requiresConnection && (
                                          <span className={`w-2 h-2 rounded-full shrink-0 ${ws.connectionAvailable ? 'bg-[#10B981]' : 'bg-[#7D8896]'}`}
                                            title={ws.connectionAvailable ? 'Connected' : 'Not connected'} />
                                        )}
                                      </div>
                                      {wi < sec.steps.length - 1 && (
                                        <div className="flex justify-center py-0.5">
                                          <div className="w-0.5 h-6 rounded-full bg-[rgba(255,255,255,0.12)]" />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                }

                /* ── Generic sections for non-upgraded templates ── */
                return (
                  <>
                    {/* What this does */}
                    <Section title="What this does" icon={Lightning} testId="section-what-this-does">
                      <p className="text-sm text-[#c0c4d0] leading-relaxed" data-testid="what-this-does-text">
                        {template.what_this_agent_does || template.short_description || cleanDescription}
                      </p>
                      {template.orion_control_note && (
                        <div className="mt-4 flex items-start gap-2.5 px-4 py-3 rounded-lg bg-[rgba(99,102,241,0.06)] border border-[rgba(99,102,241,0.12)]" data-testid="orion-control-note">
                          <Info size={14} weight="duotone" className="text-[#A78BFA] shrink-0 mt-0.5" />
                          <p className="text-sm text-[#A78BFA]">{template.orion_control_note}</p>
                        </div>
                      )}
                    </Section>

                    {/* How this works */}
                    {template.how_it_works_steps?.length > 0 && (
                      <Section title="How this works" icon={List} testId="section-how-this-works">
                        <div className="space-y-4">
                          {template.how_it_works_steps.map((step, i) => (
                            <div key={i} className="flex items-start gap-4" data-testid={`how-step-${i}`}>
                              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold"
                                style={{ backgroundColor: 'rgba(0,229,192,0.08)', color: '#00E5C0', border: '1px solid rgba(0,229,192,0.15)' }}>
                                {i + 1}
                              </div>
                              <p className="text-sm text-[#c0c4d0] leading-relaxed pt-1">{step}</p>
                            </div>
                          ))}
                        </div>
                      </Section>
                    )}

                    {/* Works even better with */}
                    {optionalTools.length > 0 && (
                      <Section title="Works even better with" icon={PlugsConnected} testId="section-works-better-with">
                        <div className="flex items-center gap-3 flex-wrap">
                          {optionalTools.slice(0, 4).map((name, i) => {
                            const isConnected = connectedApps.has(name);
                            return (
                              <button key={i} onClick={() => handleToolClick(name)} data-testid={`optional-tool-${i}`}
                                className="relative flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(0,229,192,0.3)] hover:bg-[rgba(0,229,192,0.03)] transition-all cursor-pointer group">
                                <img src={getAppLogo(name)} alt="" className="w-5 h-5 rounded object-contain" loading="lazy"
                                  onError={e => { e.target.src = getGenericAppIcon(); e.target.onerror = null; }} />
                                <span className="text-sm text-[#c0c4d0] group-hover:text-white transition-colors">{name}</span>
                                {isAuthenticated && isConnected ? (
                                  <span className="flex items-center gap-1 text-[10px] text-[#10B981] font-medium">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                                    Connected
                                  </span>
                                ) : isAuthenticated ? (
                                  <span className="text-[10px] text-[#5C6778] group-hover:text-[#00E5C0] transition-colors font-medium">Connect</span>
                                ) : null}
                              </button>
                            );
                          })}
                          {optionalTools.length > 4 && (
                            <button onClick={() => isAuthenticated ? navigate('/apps') : navigate('/register')}
                              className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg border border-dashed border-[rgba(255,255,255,0.08)] text-xs text-[#5C6778] hover:text-[#00E5C0] hover:border-[rgba(0,229,192,0.2)] transition-all cursor-pointer"
                              data-testid="more-tools-btn">
                              +{optionalTools.length - 4} more
                            </button>
                          )}
                        </div>
                      </Section>
                    )}

                    {/* About */}
                    {template.about && (
                      <Section title="About" icon={Info} testId="section-about">
                        <p className="text-sm text-[#c0c4d0] leading-relaxed">{template.about}</p>
                      </Section>
                    )}

                    {/* What you get */}
                    {useCases.length > 0 && (
                      <Section title="What you get" icon={CheckCircle} testId="section-what-you-get">
                        <div className="space-y-3">
                          {useCases.filter(uc => !/^(what this|how it|this agent|this worker)/i.test(uc.trim())).map((uc, i) => (
                            <div key={i} className="flex items-start gap-3 text-sm text-[#c0c4d0]">
                              <CheckCircle size={15} weight="duotone" className="text-[#00E5C0] shrink-0 mt-0.5" />
                              {uc}
                            </div>
                          ))}
                        </div>
                      </Section>
                    )}
                  </>
                );
              })()}

              {/* ── Preview (if available) ── */}
              {previewExamples.length > 0 && (
                <div className="rounded-xl border border-[rgba(99,102,241,0.15)] bg-[rgba(99,102,241,0.04)] p-5 mb-5" data-testid="section-preview">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkle size={14} weight="duotone" className="text-[#00E5C0]" />
                    <h3 className="text-xs font-semibold text-[#00E5C0] uppercase tracking-wider">Example</h3>
                  </div>
                  <div className="space-y-4">
                    {previewExamples.slice(0, 1).map((ex, idx) => (
                      <div key={idx} className="space-y-3" data-testid={`preview-example-${idx}`}>
                        <div className="rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] p-4">
                          <span className="text-[10px] uppercase tracking-wider text-[#00E5C0] font-semibold">{ex.input_label || 'Input'}</span>
                          <p className="text-sm text-[#c0c4d0] mt-1.5 leading-relaxed">{ex.input}</p>
                        </div>
                        <div className="flex items-center gap-2 px-4">
                          <div className="flex-1 h-px bg-[rgba(99,102,241,0.15)]" />
                          <div className="w-6 h-6 rounded-full bg-[rgba(99,102,241,0.12)] border border-[rgba(99,102,241,0.25)] flex items-center justify-center">
                            <ArrowRight size={12} className="text-[#00E5C0]" />
                          </div>
                          <div className="flex-1 h-px bg-[rgba(99,102,241,0.15)]" />
                        </div>
                        <div className="rounded-lg bg-[#1E2836] border border-[rgba(99,102,241,0.12)] p-4">
                          <span className="text-[10px] uppercase tracking-wider text-[#00E5C0] font-semibold">{ex.output_label || 'Output'}</span>
                          <p className="text-sm text-[#c0c4d0] mt-1.5 leading-relaxed">{ex.output}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Rating ── */}
              <RatingWidget slug={template.slug} initialRating={avgRating} initialCount={ratingCount} />

              {/* ── Advanced Details (collapsible) ── */}
              {capabilities.length > 0 && (
                <details className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] mb-5 group" data-testid="section-advanced">
                  <summary className="px-5 py-4 cursor-pointer select-none flex items-center gap-2 text-sm font-medium text-[#8b8fa3] hover:text-white transition-colors">
                    <GearSix size={14} weight="duotone" />
                    Advanced Details
                    <CaretRight size={12} className="ml-auto transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="px-5 pb-5 space-y-4">
                    {capabilities.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-[#8b8fa3] uppercase tracking-wider mb-2">Capabilities</h4>
                        <div className="flex flex-wrap gap-2">
                          {capabilities.map((cap, i) => (
                            <span key={i} className="px-2.5 py-1 rounded-md bg-[rgba(99,102,241,0.08)] border border-[rgba(99,102,241,0.15)] text-[#A5B4FC] text-xs" data-testid={`capability-tag-${i}`}>
                              {cap}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </details>
              )}

              {/* ── Build Similar CTA ── */}
              <div
                className="rounded-xl border border-dashed border-[rgba(0,229,192,0.3)] bg-[rgba(0,229,192,0.05)] p-6 mb-5"
                data-testid="build-similar-worker-cta"
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-white text-sm font-semibold mb-1 flex items-center gap-2">
                      <Hammer size={15} weight="duotone" className="text-[#00E5C0]" />
                      Build a similar worker
                    </h3>
                    <p className="text-xs text-[#8b8fa3]">
                      Use this as inspiration to create a custom worker tailored to your workflow.
                    </p>
                  </div>
                  <button
                    data-testid="open-in-worker-builder-btn"
                    onClick={handleBuildSimilar}
                    className="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[rgba(255,255,255,0.06)] border border-[rgba(255,255,255,0.10)] text-white text-sm font-medium hover:bg-[rgba(255,255,255,0.10)] transition-colors"
                  >
                    <Hammer size={14} weight="duotone" />
                    Open in Worker Builder
                  </button>
                </div>
              </div>

              {/* Tags */}
              {template.tags?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mb-6">
                  <Tag size={13} weight="duotone" className="text-[#555]" />
                  {template.tags.map((tag, i) => (
                    <Link
                      key={i}
                      to={`/marketplace?q=${encodeURIComponent(tag)}`}
                      className="text-[11px] text-[#8b8fa3] px-2 py-0.5 rounded bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] transition-colors"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              )}

              {/* Back link */}
              <div className="pt-6 border-t border-[rgba(255,255,255,0.08)]">
                <Link
                  to="/marketplace"
                  className="inline-flex items-center gap-2 text-sm text-[#8b8fa3] hover:text-[#00E5C0] transition-colors"
                  data-testid="back-to-gallery-link"
                >
                  <ArrowLeft size={14} weight="bold" /> Back to Marketplace
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
